/**
 * -----------------------------------------------------------------------------
 * useGeoJsonLayer
 * -----------------------------------------------------------------------------
 *
 * PURPOSE:
 * Generic hook to render and manage a GeoJSON source + layers in Mapbox.
 *
 * REPLACES:
 *   - addStacSource
 *   - addSearchResultsSource
 *   - addDetectedSearchResultsSource
 *   - addUploadedFeatures
 *   - showSearchedAoi
 *   - showMultiSearchedAoi
 *   - showDetectedObject
 *
 * FEATURES:
 *   - Auto add/update source
 *   - Fill / Line / Circle support
 *   - Feature-state hover
 *   - Click handling
 *   - Auto cleanup
 *   - Style reload resilience
 *   - Safe against empty data
 *
 * DOES NOT HANDLE:
 *   - Raster layers
 *   - Mapbox Draw
 *   - Orbit multi-line optimizations
 *
 * USAGE:
 *
 * useGeoJsonLayer(mapRef, {
 *   id: "stac",
 *   data: stacCollection,
 *   fillColor: "#5B49BC",
 *   lineColor: "#5B49BC",
 *   circleColor: "#5B49BC",
 *   hoverable: true,
 *   clickable: true,
 *   onClick: (feature, e) => {...}
 * });
 *
 * -----------------------------------------------------------------------------
 */

import { useEffect, useRef } from "react";
import { getBeforeId } from "./layerManager";

const useGeoJsonLayer = (
  mapRef,
  {
    id,
    data,
    fillColor,
    lineColor,
    circleColor,
    fillOpacity = 0.2,
    lineWidth = 1,
    circleRadius = 5,
    hoverable = false,
    clickable = false,
    onClick,
    filter,
    highlightedFeatureId = null,
  }
) => {
  // Single shared record of "who is currently hovered" so the native
  // mousemove/mouseleave handlers below and the external (e.g. list-hover)
  // trigger never stomp on state the other one set. hoverSourceRef tracks
  // which side owns the current highlight ('native' | 'external' | null) —
  // each side only ever clears a highlight it owns itself.
  const hoveredFeatureId = useRef(null);
  const hoverSourceRef = useRef(null);

  // Drives the same feature-state.hover flag the native mousemove/mouseleave
  // handlers below use, so an external trigger (e.g. hovering a list item)
  // highlights the map feature the same way hovering the map itself does.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource(id)) return;

    const hasExternal = highlightedFeatureId !== null && highlightedFeatureId !== undefined;

    if (hasExternal) {
      // Explicit external intent wins over whatever is currently hovered,
      // regardless of who set it.
      if (hoveredFeatureId.current !== null && hoveredFeatureId.current !== highlightedFeatureId) {
        map.setFeatureState({ source: id, id: hoveredFeatureId.current }, { hover: false });
      }
      map.setFeatureState({ source: id, id: highlightedFeatureId }, { hover: true });
      hoveredFeatureId.current = highlightedFeatureId;
      hoverSourceRef.current = "external";
    } else if (hoverSourceRef.current === "external") {
      // Only clear what external itself set — a native map hover may have
      // taken over in the meantime, and that isn't ours to touch.
      if (hoveredFeatureId.current !== null) {
        map.setFeatureState({ source: id, id: hoveredFeatureId.current }, { hover: false });
      }
      hoveredFeatureId.current = null;
      hoverSourceRef.current = null;
    }
  }, [highlightedFeatureId, id, data, mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    
    let normalizedData = null;
    
    if (!data) return;
    
    // Geometry → wrap into Feature
    if (data.type && data.coordinates) {
      normalizedData = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: data,
            properties: {},
          },
        ],
      };
    }
    // Feature → wrap into FeatureCollection
    else if (data.type === "Feature") {
      normalizedData = {
        type: "FeatureCollection",
        features: [data],
      };
    }
    // Already FeatureCollection
    else if (data.type === "FeatureCollection") {
      normalizedData = data;
    }
    
    if (!normalizedData || !normalizedData.features) return;
    
    const beforeId = getBeforeId(map, id);
    const addLayer = () => {
      if (!map.getSource(id)) {
        map.addSource(id, {
          type: "geojson",
          data,
          generateId: true,
        });
      } else {
        map.getSource(id).setData(data);
      }

      // FILL
      if (fillColor && !map.getLayer(`${id}-fill`)) {
        map.addLayer({
          id: `${id}-fill`,
          type: "fill",
          source: id,
          paint: {
            "fill-color": fillColor,
            "fill-opacity": hoverable
              ? [
                  "case",
                  ["boolean", ["feature-state", "hover"], false],
                  0.7,
                  fillOpacity,
                ]
              : fillOpacity,
          },
          filter: ["==", "$type", "Polygon"],
          beforeId,
        });
      }

      // LINE
      if (lineColor && !map.getLayer(`${id}-line`)) {
        map.addLayer({
          id: `${id}-line`,
          type: "line",
          source: id,
          paint: {
            "line-color": lineColor,
            "line-width": lineWidth,
          },
          beforeId,
        });
      }

      // POINT
      if (circleColor && !map.getLayer(`${id}-circle`)) {
        map.addLayer({
          id: `${id}-circle`,
          type: "circle",
          source: id,
          paint: {
            "circle-radius": circleRadius,
            "circle-color": circleColor,
          },
          filter: ["==", "$type", "Point"],
          beforeId,
        });
      }

      attachInteractions(map);
    };

    if (!map.isStyleLoaded()) {
      map.once("styledata", addLayer);
    } else {
      addLayer();
    }

    function attachInteractions(map) {
      if (hoverable) {
        map.on("mousemove", `${id}-fill`, (e) => {
          const feature = e.features?.[0];
          if (!feature) return;

          if (hoveredFeatureId.current !== null && hoveredFeatureId.current !== feature.id) {
            map.setFeatureState(
              { source: id, id: hoveredFeatureId.current },
              { hover: false }
            );
          }

          hoveredFeatureId.current = feature.id;
          hoverSourceRef.current = "native";

          map.setFeatureState({ source: id, id: feature.id }, { hover: true });
        });

        map.on("mouseleave", `${id}-fill`, () => {
          // Only clear what native itself set — an external (list-hover)
          // highlight isn't ours to touch.
          if (hoverSourceRef.current !== "native") return;

          if (hoveredFeatureId.current !== null) {
            map.setFeatureState(
              { source: id, id: hoveredFeatureId.current },
              { hover: false }
            );
          }
          hoveredFeatureId.current = null;
          hoverSourceRef.current = null;
        });
      }

      if (clickable && onClick) {
        map.on("click", `${id}-fill`, (e) => {
          const feature = e.features?.[0];
          if (feature) onClick(feature, e);
        });

        map.on("click", `${id}-circle`, (e) => {
          const feature = e.features?.[0];
          if (feature) onClick(feature, e);
        });
      }
    }

    return () => {
      const map = mapRef?.current;
    
      // If map already destroyed → stop immediately
      if (!map) return;
    
      // If style already removed → stop
      if (!map.isStyleLoaded || !map.isStyleLoaded()) return;
    
      const safeRemoveLayer = (layerId) => {
        try {
          if (map.getLayer && map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        } catch (e) {
          // Ignore errors if style already gone
        }
      };
    
      const safeRemoveSource = (sourceId) => {
        try {
          if (map.getSource && map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        } catch (e) {
          // Ignore errors
        }
      };
    
      safeRemoveLayer(`${id}-fill`);
      safeRemoveLayer(`${id}-line`);
      safeRemoveLayer(`${id}-circle`);
      safeRemoveSource(id);
    };
  }, [data, id]);

  return null;
};

export default useGeoJsonLayer;
